import { useEffect, useState } from "react";
import { getRequest } from "utils/httpClient";
import { useAuth } from "hooks/auth";
import { useAppDispatch, useAppSelector } from "state/hooks";
import {
  getGroupState,
  getGroupStatus,
  requestToJoinGroup,
} from "state/slices/group-slice";
import Header from "components/header";
import LoadingSpinner from "components/loading-spinner.tsx";
import Headroom from "react-headroom";
import { Pool } from "interfaces/pool";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import "react-bootstrap-typeahead/css/Typeahead.css";
import JoinGroupButton from "./join-group-button";
import SetGroupStateFromSlug from "app-setup/set-group-state-from-slug";
import SetupMixpanel from "app-setup/mixpanel-init";
import LoginButtons from "components/login-buttons";
import {
  getPoolsPath,
  getEventsPath,
  getEventGroupConfigsPath,
} from "utils/backend-path-builders";
import { EventsResponse } from "interfaces/events-response";
import { EventGroupConfig } from "interfaces/event-group-config";
import { POOL_STATUS_PARAM, SUCCEEDED } from "utils/constants";
import { getUserState } from "state/slices/user-slice";
import UsernamePrompt from "./username-form/username-prompt";
import JoinGroupModal from "./join-group-button/join-group-modal";
import { SparketsBanner, ViewMarketplaceButton } from "@wagerwire/embed/react";
import InitializeIntegrationApp from "app-setup/initialize-integration-app";
import {
  getIntegratedAppState,
  IntegratedAppState,
} from "state/slices/integrated-app-slice";
import LoadingGroupPage from "components/loading-group-page";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus";
import {
  clearHomePageEventIdFilter,
  getUserGroupSettingsByGroupId,
  setHomePageEventIdFilter,
} from "state/slices/user-settings-slice";
import useQueryParams from "utils/useQueryParams";
import { EVENT_ID_QUERY_PARAM } from "home/home-page-query-parameters";
import { useParams } from "react-router-dom";
import FeaturedEventsCarousel from "home/event-filters/featured-events-carousel";
import HomepageEventFilter from "home/event-filters/homepage-event-filter";
import HomepagePoolsList from "components/homepage-pools-list";
import { EventIdAndName } from "interfaces/event-id-and-name.ts";
import useWagerWire from "hooks/useWagerWire";

const EVENT_ID_QUERY_PARAM_CLEAR_EVENT_FILTER_REQUEST_VALUE = "";

const Home = () => {
  const { groupSlug } = useParams<{ groupSlug: string }>();

  const auth = useAuth();
  const queryParams = useQueryParams();

  const [eventFilter, setEventFilter] = useState<EventIdAndName>();
  const [pools, setPools] = useState<Pool[]>([]);
  const [poolsLoaded, setPoolsLoaded] = useState(false);
  const [loadingPools, setLoadingPools] = useState(true);
  const group = useAppSelector(getGroupState);
  const [uniqueEvents, setUniqueEvents] = useState<EventIdAndName[]>([]);
  const [eventIconMap, setEventIconMap] = useState<Map<string, string>>(
    new Map()
  );
  const integratedAppState = useAppSelector(getIntegratedAppState);

  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false);
  const dispatch = useAppDispatch();
  const groupStatus = useAppSelector(getGroupStatus);
  const [poolDataIsStale, setPoolDataIsStale] = useState(false);
  const userGroupSettings = useAppSelector((state) =>
    getUserGroupSettingsByGroupId(state, group.id)
  );
  const user = useAppSelector(getUserState);
  const { wagerWireClosed, setWagerWireClosed } = useWagerWire();

  SetupMixpanel();

  SetGroupStateFromSlug(groupSlug);

  InitializeIntegrationApp(groupSlug);

  useEffect(() => {
    const updateEventIdFilterUserSettings = () => {
      if (!group.id) {
        return;
      }
      const eventIdQueryParam = queryParams.get(EVENT_ID_QUERY_PARAM);

      if (eventIdQueryParam === null) return;

      if (
        eventIdQueryParam ===
        EVENT_ID_QUERY_PARAM_CLEAR_EVENT_FILTER_REQUEST_VALUE
      ) {
        dispatch(clearHomePageEventIdFilter({ groupId: group.id }));
      } else {
        dispatch(
          setHomePageEventIdFilter({
            groupId: group.id,
            eventId: eventIdQueryParam,
          })
        );
      }
    };

    updateEventIdFilterUserSettings();
  }, [queryParams, dispatch, group.id]);

  useEffect(() => {
    const syncUserSettingsAndEventFilter = () => {
      if (!poolsLoaded) {
        return;
      }

      const eventFilter = uniqueEvents.find(
        (event) => event.id === userGroupSettings?.homePagePoolsEventIdFilter
      );

      if (!eventFilter) {
        dispatch(clearHomePageEventIdFilter({ groupId: group.id }));
        setEventFilter(undefined);
      } else {
        setEventFilter(eventFilter);
      }
    };

    syncUserSettingsAndEventFilter();
  }, [
    dispatch,
    userGroupSettings?.homePagePoolsEventIdFilter,
    uniqueEvents,
    poolsLoaded,
    integratedAppState.state,
    group.id,
  ]);

  useEffect(() => {
    setWagerWireClosed(false);

    const fetchPools = () => {
      if (!group.id) {
        return;
      }
      const path = getPoolsPath(group.id);
      getRequest(path, {
        queryParams: {
          status: POOL_STATUS_PARAM.OPEN,
          pg: 1,
          page_size: 10000,
          sortBy: "weight:desc,close:asc",
        },
        skipIntegrationApi: true,
      })
        .then((poolsResponse: Pool[]) => {
          setPools(poolsResponse);
          setPoolDataIsStale(false);

          const eventIdAndNameByEventId = new Map(
            poolsResponse
              .filter((p) => p.event_name && p.event_id)
              .map((p) => [
                p.event_id,
                { id: p.event_id, name: p.event_name } as EventIdAndName,
              ])
          );

          const uniqueEvents = Array.from(eventIdAndNameByEventId.values());

          setUniqueEvents(uniqueEvents);
        })
        .finally(() => {
          setPoolsLoaded(true);
          setLoadingPools(false);
        });
    };

    fetchPools();
  }, [group.id, poolDataIsStale, wagerWireClosed]);

  // Fetch events and event group configs to build icon map for pool items
  useEffect(() => {
    if (!group.id) return;

    Promise.all([
      getRequest(getEventsPath(), {
        queryParams: { "group-id": group.id },
        skipIntegrationApi: true,
      }),
      getRequest(getEventGroupConfigsPath(), {
        queryParams: { "group-id": group.id },
        skipIntegrationApi: true,
      }),
    ])
      .then(([events, configs]: [EventsResponse[], EventGroupConfig[]]) => {
        const iconMap = new Map<string, string>();
        events.forEach((event) => {
          const eventConfig = configs.find((c) => c.event_id === event.id);
          // Prefer group-specific icon, fall back to global icon
          const iconUrl =
            eventConfig?.settings?.icon_url || event.settings?.icon_url;
          if (iconUrl) {
            iconMap.set(event.id, iconUrl);
          }
        });
        setEventIconMap(iconMap);
      })
      .catch((error) => {
        console.error("Error fetching event icons:", error);
      });
  }, [group.id]);

  if (!group.id) {
    return <LoadingGroupPage />;
  }

  const filteredPools = eventFilter
    ? pools.filter((pool) => pool.event_id === eventFilter.id)
    : pools;

  if (
    loadingPools ||
    ((integratedAppState.state === IntegratedAppState.NOT_INTEGRATED || integratedAppState.state === IntegratedAppState.UNDEFINED) &&
      (auth.pending || (auth.signedIn && user.fetchUserState !== SUCCEEDED)))
  ) {
    return <LoadingSpinner />;
  } else {
    return (
      <>
        <Headroom>
          <div>
            <Header />
            <LoginButtons />
          </div>
        </Headroom>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap-reverse",
          }}
        >
          {group.settings.wager_wire && (
            <div
              style={{
                paddingLeft: 12,
                paddingRight: 12,
                width: "100%",
                boxSizing: "border-box",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <ViewMarketplaceButton
                groupColor={group.settings.primary_color}
                groupId={group.id}
                style={{
                  width: "100%",
                  maxWidth: "95vw",
                  borderRadius: 5,
                  overflow: "hidden",
                  padding: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div style={{ width: "100%" }}>
                  <SparketsBanner
                    groupColor={group.settings.secondary_color}
                    style={{ width: "100%", display: "block" }}
                  />
                </div>
              </ViewMarketplaceButton>
            </div>
          )}
          {pools.length > 0 && (
            <>
              <HomepageEventFilter
                uniqueEvents={uniqueEvents}
                eventFilter={eventFilter}
              />
              <FeaturedEventsCarousel
                uniqueEvents={uniqueEvents}
                eventFilter={eventFilter}
              />
            </>
          )}
          {auth.signedIn && (
            <div
              style={{
                paddingTop: 10,
                paddingBottom: 5,
                marginLeft: "auto",
              }}
            >
              {integratedAppState.state === IntegratedAppState.NOT_INTEGRATED && <JoinGroupButton />}
            </div>
          )}
        </div>
        {filteredPools.length > 0 ? (
          <HomepagePoolsList
            pools={filteredPools}
            refetchPoolData={() => setPoolDataIsStale(true)}
            eventIconMap={eventIconMap}
          />
        ) : (
          <BlockyHeavyText
            customStyles={{
              display: "flex",
              justifyContent: "center",
              color: "white",
            }}
          >
            No pools are currently open. Please check back later.
          </BlockyHeavyText>
        )}
        <UsernamePrompt
          onClose={() => {
            if (groupStatus === UserGroupMembershipStatus.NOT_REQUESTED) {
              setShowJoinGroupModal(true);
            }
          }}
        />

        <JoinGroupModal
          acceptAndJoin={() => {
            dispatch(requestToJoinGroup(group.id));
            setShowJoinGroupModal(false);
          }}
          show={showJoinGroupModal}
          close={() => setShowJoinGroupModal(false)}
        />
      </>
    );
  }
};

export default Home;

